import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api, { money, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import { InclusionsField } from "@/components/InclusionsField";
import { CheckCircle2, XCircle, Loader2, Truck, MapPin, ArrowDownRight, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";

export default function WhatsAppBid() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({ vendor_name: "", rate: "", inclusions: "" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/whatsapp/${token}`).then((r) => setData(r.data)).catch((e) => setErr(apiErr(e)));
  }, [token]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/whatsapp/${token}/quote`, {
        vendor_name: form.vendor_name,
        rate: parseFloat(form.rate),
        inclusions: form.inclusions,
      });
      setDone(true);
      toast.success("Quote submitted — thank you!");
    } catch (er) { toast.error(apiErr(er)); } finally { setBusy(false); }
  };

  if (err) return <Center><div className="text-center px-6"><XCircle className="h-10 w-10 text-destructive mx-auto mb-3" /><p className="text-muted-foreground">{err}</p></div></Center>;
  if (!data) return <Center><Loader2 className="h-8 w-8 animate-spin text-primary" /></Center>;

  const sl = data.service_lines || {};
  const services = ["loading", "unloading", "packaging"].filter((k) => sl[k]);

  return (
    <div className="min-h-screen bg-muted py-6 px-4">
      <div className="max-w-md mx-auto bg-card border border-border" data-testid="wa-bid-page">
        <div className="flex items-center justify-between p-5 border-b-4 border-primary">
          <Logo className="h-9" />
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Order</div>
            <div className="font-mono-num font-bold text-primary text-sm" data-testid="wa-order-number">{data.order_number}</div>
            {data.lr_number && <div className="font-mono-num text-[10px] text-muted-foreground">LR {data.lr_number}</div>}
          </div>
        </div>

        <div className="p-5 space-y-4">
          <div className="text-sm text-muted-foreground">You've received a transport quote request. Trip details below — <span className="font-medium text-foreground">customer details are private</span>.</div>

          <div className="space-y-2" data-testid="wa-route">
            <RouteBlock icon={ArrowUpRight} label="Pickup (From)" p={data.pickup} />
            <RouteBlock icon={ArrowDownRight} label="Drop (To)" p={data.delivery} />
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Box label="Category" value={<span className="capitalize">{data.category}</span>} />
            <Box label="Move date" value={data.move_date || "Flexible"} />
          </div>

          <div>
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Goods</div>
            <div className="border border-border divide-y divide-border">
              {(data.items || []).map((it, i) => <div key={i} className="flex justify-between p-2 px-3 text-sm"><span>{it.name}</span><span className="font-mono-num text-muted-foreground">×{it.quantity}</span></div>)}
              {(!data.items || !data.items.length) && <div className="p-2 px-3 text-sm text-muted-foreground">Not specified</div>}
            </div>
            {services.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{services.map((s) => <span key={s} className="capitalize text-[11px] border border-border px-2 py-1 bg-muted">{s} required</span>)}</div>}
          </div>

          {data.locked ? (
            <div className="border border-border bg-muted p-4 text-center text-sm text-muted-foreground"><Truck className="h-6 w-6 mx-auto mb-2 text-primary" />This order is already confirmed. Bidding is closed.</div>
          ) : done ? (
            <div className="border border-emerald-500/40 bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 p-5 text-center" data-testid="wa-success">
              <CheckCircle2 className="h-9 w-9 mx-auto mb-2" />
              <div className="font-bold">Quote received!</div>
              <div className="text-sm opacity-80">Arrow Logistics will review your rate of {money(form.rate)} and get back to you.</div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4 pt-1" data-testid="wa-quote-form">
              <div className="space-y-1.5"><Label>Your name / company (optional)</Label>
                <Input className="rounded-none h-11" value={form.vendor_name} onChange={(e) => set("vendor_name", e.target.value)} placeholder="e.g. Sharma Transport" data-testid="wa-vendor-name" /></div>
              <div className="space-y-1.5"><Label>Proposed rate (₹) *</Label>
                <Input type="number" min="1" step="0.01" className="rounded-none h-12 font-mono-num text-lg" value={form.rate} onChange={(e) => set("rate", e.target.value)} placeholder="e.g. 16500" required data-testid="wa-rate" /></div>
              <InclusionsField value={form.inclusions} onChange={(v) => set("inclusions", v)} testid="wa-inclusions" />
              <Button type="submit" disabled={busy} className="w-full h-12 rounded-none font-bold uppercase tracking-wide" data-testid="wa-submit">{busy ? "Submitting…" : "Submit Quote"}</Button>
            </form>
          )}
        </div>
        <div className="px-5 py-3 border-t border-border text-center text-[11px] text-muted-foreground">Arrow Logistics · Secure partner bidding</div>
      </div>
    </div>
  );
}

const RouteBlock = ({ icon: Icon, label, p = {} }) => (
  <div className="border border-border p-3">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1 mb-1">
      {Icon ? <Icon className="h-3 w-3 text-primary" /> : <MapPin className="h-3 w-3 text-primary" />}{label}
    </div>
    <div className="font-medium text-sm">{p.full || "—"}</div>
    <div className="text-xs text-muted-foreground mt-1">Floor Level: <span className="text-foreground">{p.floor || "—"}</span></div>
    <div className="text-xs text-muted-foreground">{p.lift_text || "Lift Not Available"}</div>
  </div>
);
const Box = ({ label, value }) => (
  <div className="border border-border p-2.5">
    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
    <div className="font-medium mt-0.5 text-sm">{value}</div>
  </div>
);
const Center = ({ children }) => <div className="min-h-screen grid place-items-center bg-background">{children}</div>;
